import React, { useState } from "react";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import { ExternalLink, HelpCircle, Plus, Unlink } from "lucide-react";
import BaseIntegrationInject from "@reusables/BaseIntegrationInject";
import { ReactComponent as CheckedIcon } from "@assets/icons/checked.svg";
import { Trans, useTranslation } from "react-i18next";
import UnlinkInvoiceModal from "./UnlinkInvoiceModal";
import { PurchaseOrder } from "@/types/general";
import { ArrayElementType } from "@helpers/utils";
import { Menu, MenuItem } from "@mui/material";
import BaseTooltip from "@/components/reusables/BaseTooltip";
import AddInvoiceModal
    from "@components/Dashboard/pages/PurchasingPage/PurchasesPage/components/ViewLayout/parts/InvoicesSection/AddInvoiceModal";
import SetReadyForPaymentModal
    from "@components/Dashboard/pages/PurchasingPage/PurchasesPage/components/ViewLayout/parts/InvoicesSection/SetReadyForPaymentModal";
import { Can, useAbility } from "@/casl.config";

type InvoicesSectionProps = {
    purchaseOrder: PurchaseOrder.Extended;
}

type Invoice = ArrayElementType<PurchaseOrder.Extended["invoices"]>;

export default function InvoicesSection({ purchaseOrder }: InvoicesSectionProps) {
    const { t } = useTranslation("", { keyPrefix: "purchaseOrder.general.invoices" });

    const ability = useAbility();

    const [isAddInvoiceModalOpen, setIsAddInvoiceModalOpen] = useState(false);

    const [isUnlinkInvoiceModalOpen, setIsUnlinkInvoiceModalOpen] = useState(false);
    const [unlinkInvoice, setUnlinkInvoice] = useState<Invoice>();

    const [isSetReadyForPaymentModalOpen, setIsSetReadyForPaymentModalOpen] = useState(false);
    const [setReadyForPaymentInvoice, setSetReadyForPaymentInvoice] = useState<Invoice>();

    if (ability.cannot("invoice.view", "purchase_order"))
        return null;

    return (
        <>
            <div className="flex flex-col space-y-[16px]">
                <div className="flex items-center justify-between">
                    <div className="text-xl text-accent">{t("heading")}</div>
                    <BaseMaterialIconButton
                        icon={<Plus size={16} />}
                        size={"small"}
                        onClick={() => setIsAddInvoiceModalOpen(true)}
                    />
                </div>
                <div className="space-y-2">
                    {
                        purchaseOrder.invoices.length !== 0 ?
                            purchaseOrder.invoices.map((invoice) => (
                                <div
                                    key={invoice.system + "_" + invoice.system_id}
                                    className="flex items-center border border-solid border-[#D9D6DA] rounded-[8px] py-[8px] px-[12px]">
                                    <BaseIntegrationInject integration={"tripletex"}>
                                        {
                                            (integration) =>
                                                <img
                                                    src={integration?.imageUrl}
                                                    alt={integration?.name}
                                                    className="w-6 h-6 p-[2px] bg-white rounded-sm object-contain"
                                                />
                                        }
                                    </BaseIntegrationInject>
                                    <div className="text-accent ml-[8px]">
                                        {invoice.number}
                                    </div>
                                    {
                                        invoice.status === "ready_for_payment" && (
                                            <div
                                                className={"flex flex-row items-center bg-[#CDEC9B] bg-opacity-[.36] rounded-full py-[2px] px-[8px] text-[#70B500] text-sm font-medium ml-[24px]"}>
                                                <CheckedIcon width={18} height={18} className={"mr-[4px]"} />
                                                <span>{t("statuses.readyForPayment")}</span>
                                            </div>
                                        )
                                    }
                                    <div className={"grow text-right space-x-4"}>
                                        {
                                            !!invoice.link &&
                                            <a href={invoice.link}>
                                                <ExternalLink
                                                    className={"text-gray-300 hover:text-gray-600 cursor-pointer"} />
                                            </a>
                                        }
                                        <Can I={"invoice.edit"} a={"purchase_order"}>
                                            <InvoiceMenu
                                                items={[
                                                    {
                                                        label: t("menu.unlink.label"),
                                                        icon: <Unlink size={14} />,
                                                        onClick: () => {
                                                            setIsUnlinkInvoiceModalOpen(true);
                                                            setUnlinkInvoice(invoice);
                                                        }
                                                    },
                                                    ...(invoice.status === "pending" ? [{
                                                            label: t("menu.readyForPayment.label"),
                                                            icon: <CheckedIcon width={14} height={14} />,
                                                            onClick: () => {
                                                                setIsSetReadyForPaymentModalOpen(true);
                                                                setSetReadyForPaymentInvoice(invoice);
                                                            },
                                                            tooltip: <Trans
                                                                i18nKey={"menu.readyForPayment.tooltip"}
                                                                t={t} components={{ b: <b /> }}
                                                            />
                                                        }] : []
                                                    )
                                                ]}
                                            />
                                        </Can>
                                    </div>
                                </div>
                            ))
                            :
                            <div
                                className="flex items-center justify-center text-xl font-thin text-notFound-blocks uppercase border border-solid border-[#D9D6DA] rounded-[8px] py-[8px] px-[12px]"
                                style={{ height: 100 }}
                            >
                                {t("noInvoices")}
                            </div>
                    }
                </div>
            </div>
            <Can I={"invoice.edit"} a={"purchase_order"}>
                <AddInvoiceModal
                    isOpen={isAddInvoiceModalOpen}
                    onClose={() => setIsAddInvoiceModalOpen(false)}
                    purchaseOrderId={purchaseOrder.id}
                />
                <UnlinkInvoiceModal
                    isOpen={isUnlinkInvoiceModalOpen}
                    onClose={() => setIsUnlinkInvoiceModalOpen(false)}
                    purchaseOrderId={purchaseOrder.id}
                    invoice={
                        unlinkInvoice ? {
                            systemId: unlinkInvoice.system_id,
                            system: unlinkInvoice.system,
                            number: unlinkInvoice.number
                        } : undefined
                    }
                />
                <SetReadyForPaymentModal
                    isOpen={isSetReadyForPaymentModalOpen}
                    onClose={() => setIsSetReadyForPaymentModalOpen(false)}
                    purchaseOrderId={purchaseOrder.id}
                    invoice={
                        setReadyForPaymentInvoice ? {
                            systemId: setReadyForPaymentInvoice.system_id,
                            system: setReadyForPaymentInvoice.system,
                            number: setReadyForPaymentInvoice.number
                        } : undefined
                    }
                />
            </Can>
        </>
    );
}

type InvoiceMenuProps = {
    items: {
        label: string | React.ReactNode,
        icon?: React.ReactNode,
        onClick?: () => void,
        tooltip?: string | React.ReactNode;
    }[],
}

export function InvoiceMenu({ items }: InvoiceMenuProps) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    return (
        <div className={"inline-block"}>
            <div
                className="flex space-x-[4px] justify-center align-center py-[10px] cursor-pointer group"
                onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                }}>
                {
                    [...Array(3)].map((x, i) => <div key={i}
                                                     className={`bg-gray-300 rounded-full w-[5px] h-[5px] group-hover:bg-gray-600 transition-[.15s]`}></div>)
                }
            </div>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{
                    "aria-labelledby": "invoice-menu-button"
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
                sx={{
                    ".MuiPaper-root": {
                        border: "1px solid #E5E7EB",
                        ".MuiMenuItem-root": {
                            color: "#3C3769"
                        }
                    }
                }}
            >
                {
                    items.map((item, i) => (
                        <MenuItem
                            key={i}

                            onClick={() => {
                                setAnchorEl(null);
                                item.onClick?.();
                            }}

                            className={"flex justify-between items-center"}
                        >
                            <div className={"flex items-center space-x-[10px]"}>
                                {item.icon}
                                <span>{item.label}</span>
                            </div>
                            {
                                !!item.tooltip &&
                                <div className={"!ml-8"}>
                                    <BaseTooltip title={item.tooltip}>
                                        <HelpCircle size={14} color={"#A3A5BA"} />
                                    </BaseTooltip>
                                </div>
                            }
                        </MenuItem>
                    ))
                }
            </Menu>
        </div>
    );
}
